import { code, table, wrappingBlock } from "@/lib/codeUtils";
import type { RestorationBlock, RestorationPostBody } from "@/lib/server-actions/restoration/_shared";

const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 800;

/**
 * 복원 게시판 문제 메타에서 쓰이는 카테고리 옵션. 관리자가 1~20번 문제마다
 * 지정한다. `restoration_problem_meta.category`는 자유 텍스트이지만 UI에서는
 * 이 화이트리스트만 노출해 일관성을 유지한다.
 */
export const RESTORATION_CATEGORY_OPTIONS = [
  "C언어",
  "자바",
  "파이썬",
  "DB",
  "네트워크/OS",
  "SW개발",
  "SW설계",
  "보안/신기술",
] as const;

export type RestorationCategory = (typeof RESTORATION_CATEGORY_OPTIONS)[number];

function escapeAttr(value: string): string {
  return value.replace(/"/g, "&quot;");
}

function toMarkdownTable(headers: string[], rows: string[][]): string {
  const safeHeaders = headers.length > 0 ? headers : [""];
  const cellWidth = safeHeaders.length;
  const headerLine = `| ${safeHeaders.map((h) => h.trim() || " ").join(" | ")} |`;
  const separatorLine = `| ${safeHeaders.map(() => "---").join(" | ")} |`;
  const rowLines = rows.map((row) => {
    const padded = [...row];
    while (padded.length < cellWidth) padded.push("");
    return `| ${padded.slice(0, cellWidth).map((c) => c.trim() || " ").join(" | ")} |`;
  });
  return [headerLine, separatorLine, ...rowLines].join("\n");
}

/**
 * RestorationBlock[]을 ProblemAnswer의 `problem` prop 문자열로 변환한다.
 * - text: 원문 그대로
 * - code: codeUtils.code() 헬퍼로 Shiki 처리 가능한 코드 블록
 * - table: markdown 표 → codeUtils.table()로 래핑
 * - image: <ImageWithCaption /> JSX 문자열 (Next.js Image 요구사항에 맞춰 width/height 필수)
 */
export function blocksToProblemString(blocks: RestorationBlock[]): string {
  return blocks
    .map((block) => {
      switch (block.type) {
        case "text":
          return block.content.trim();
        case "code":
          return code(block.language?.trim() || "txt", block.content);
        case "table":
          return table(
            "overflow-x-auto",
            toMarkdownTable(block.headers, block.rows)
          );
        case "image": {
          if (!block.url) {
            return wrappingBlock(
              "h-48 w-full bg-gray-200 dark:bg-neutral-700 rounded-lg animate-pulse text-sm text-gray-500 dark:text-neutral-400 justify-center",
              "이미지 업로드 대기 중..."
            );
          }
          const width = block.width ?? DEFAULT_IMAGE_WIDTH;
          const height = block.height ?? DEFAULT_IMAGE_HEIGHT;
          const altAttr = escapeAttr(block.alt ?? "첨부 이미지");
          return `<ImageWithCaption src="${escapeAttr(block.url)}" alt="${altAttr}" width={${width}} height={${height}} showCaption={false} />`;
        }
        default:
          return "";
      }
    })
    .filter((s) => s.length > 0)
    .join("\n\n");
}

/**
 * 서버의 validateBody를 미러하는 클라이언트용 UX 검증. submit 직전에만 돌린다.
 * 반환값: 에러 메시지 또는 null.
 */
export function validateBodyClient(body: RestorationPostBody): string | null {
  if (!Array.isArray(body.blocks)) return "본문 블록이 비어 있습니다.";
  if (body.blocks.length === 0) return "최소 1개 이상의 블록이 필요합니다.";

  for (const block of body.blocks) {
    if (block.type === "text" && !block.content.trim()) {
      return "비어 있는 텍스트 블록이 있습니다.";
    }
    if (block.type === "code" && !block.content.trim()) {
      return "비어 있는 코드 블록이 있습니다.";
    }
    if (block.type === "table") {
      if (!block.headers || block.headers.length === 0) {
        return "표에는 최소 1개의 헤더가 필요합니다.";
      }
    }
    if (block.type === "image" && !block.url) {
      return "업로드 중이거나 URL이 없는 이미지 블록이 있습니다.";
    }
  }

  if (body.choices) {
    if (!Array.isArray(body.choices)) return "보기는 배열이어야 합니다.";
    const emptyChoice = body.choices.some((c) => !c.trim());
    if (emptyChoice) return "비어 있는 보기가 있습니다.";
  }

  if (body.correctAnswer !== undefined) {
    if (typeof body.correctAnswer === "string") {
      if (!body.correctAnswer.trim()) return "정답을 입력해주세요.";
    } else if (Array.isArray(body.correctAnswer)) {
      if (body.correctAnswer.length === 0) return "정답을 하나 이상 선택해주세요.";
    }
  }

  return null;
}
